import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import OfficialDiaryAppService from '../../../../application/platform/sdp/official-diary/official-diary-app-service';
import { IPreviewPublicationRequest } from '../../../../model/sdp/official-daily/request/preview-publication-request';
import { IConfirmPublicationRequest } from '../../../../model/sdp/official-daily/request/confirm-publication-request';
import { ACTION_TYPES, useOfficialDiaryPublicationDispatch, useOfficialDiaryPublicationState } from "./official-diary-publication-context";
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { DefaultDialogProps } from '../../../../components/default-dialog';

export const useOfficialDiaryPublicationController = () => {
    const navigate = useNavigate();
    const { showLoading, showMessage, showDialog } = useBaseLayout();
    const dispatch = useOfficialDiaryPublicationDispatch();
    const state = useOfficialDiaryPublicationState();
    const { submissionPublicationId } = useParams();

    const steps = ['Dados da Publicação', 'Visualizar Publicação'];

    function totalSteps() {
        return steps.length;
    }

    function completedSteps() {
        return Object.keys(state.completed).length;
    }

    function allStepsCompleted() {
        return completedSteps() === totalSteps();
    }

    function isLastStep() {
        return state.activeStep === totalSteps() - 1;
    }

    function setRequestPreviewParameters(params: IPreviewPublicationRequest) {
        dispatch({
            type: ACTION_TYPES.setPreviewPublicationRequest,
            payload: params
        });
    }

    function setFilesUpload(files: File[]) {
        dispatch({
            type: ACTION_TYPES.setFiles,
            payload: files
        });
    }

    async function requestPreviewPublicationRequest() {
        try {
            showLoading({ open: true });
            dispatch({ type: ACTION_TYPES.setLoading, payload: true });

            const data = await OfficialDiaryAppService.previewPublication(state.previewPublicationRequest, state.files);

            dispatch({
                type: ACTION_TYPES.setPreviewPublicationResponse,
                payload: data
            });

            dispatch({ type: ACTION_TYPES.setActiveStep, payload: 1 });

            showLoading({ open: false });
            dispatch({ type: ACTION_TYPES.setLoading, payload: false });
        } catch (error) {
            console.error("Fail request", error);
            showLoading({ open: false });
            dispatch({ type: ACTION_TYPES.setLoading, payload: false });
        }
    }

    async function requestConfirmPublicationRequest() {
        try {
            let requestModel = Object.assign({} as IConfirmPublicationRequest, state.previewPublicationResponse);
            requestModel.submissionPublicationId = Number(submissionPublicationId);

            showLoading({ open: true });

            await OfficialDiaryAppService.confirmPublication(requestModel);

            showLoading({ open: false });

            showDialog({
                open: true,
                message: "Publicação salva com suceso. Deseja diagramar outra publicação?",
                onYesClick: () => {
                    dispatch({ type: ACTION_TYPES.resetForm, payload: null });
                },
                onNoClick: () => {
                    navigate('/diario-oficial');
                },
            } as DefaultDialogProps);
        } catch (error) {
            console.error(error);
            showLoading({ open: false });
        }
    }

    function validFields(): boolean {
        let isValid = true;

        if (state.previewPublicationRequest.categoryId === undefined || state.previewPublicationRequest.categoryId === 0) {
            isValid = false;
        }
        if (state.previewPublicationRequest.subcategoryId === undefined || state.previewPublicationRequest.subcategoryId === 0) {
            isValid = false;
        }
        if (state.previewPublicationRequest.publicationDateOn === undefined || state.previewPublicationRequest.publicationDateOn === "") {
            isValid = false;
        }

        return isValid;
    }

    function handleNext() {
        if (!validFields()) {
            showMessage({ type: 'error', message: 'É necessário preencher todos os campos obrigatórios.', open: true });
            return;
        }

        if (state.activeStep === 0) {
            requestPreviewPublicationRequest();
        } else {
            requestConfirmPublicationRequest();
        }
    }

    function handleBack() {
        dispatch({
            type: ACTION_TYPES.setActiveStep,
            payload: state.activeStep - 1
        });
    }

    function handleStep(step: number) {
        dispatch({
            type: ACTION_TYPES.setActiveStep,
            payload: step
        });
    }

    function navigateBack() {
        navigate('/diario-oficial');
    }

    return {
        steps,
        totalSteps,
        completedSteps,
        allStepsCompleted,
        isLastStep,
        setRequestPreviewParameters,
        setFilesUpload,
        handleNext,
        handleBack,
        handleStep,
        navigateBack,
        requestConfirmPublicationRequest,
    };
}
